import { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
  Tooltip,
} from '@material-ui/core';
import LockIcon from '@material-ui/icons/Lock';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Page, Content, InfoCard, Link } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { RebacExplorerClient } from '../../api/RebacExplorerClient';
import type { Grant, GrantRelation } from '../../api/RebacExplorerClient';
import { ManageAccessButton } from '../PermissionsTab/TemplateManageButton';
import { useCanViewPlugin } from '../../hooks/useCanViewPlugin';

// catalog é o nome de objeto pré-FLWP-68588 — mantido aqui até a migração de
// dados (scripts/migrate-openfga-plugin-catalog-rename.mjs) rodar em todo
// store; depois disso o OpenFGA nunca mais devolve esse id e a entrada fica
// morta (inofensiva, pode ser removida então). `scaffolder` já foi removido:
// o objeto FGA correspondente (plugin:scaffolder) não existe mais em nenhum
// store — todo o namespace scaffolder.* é checado hoje contra
// plugin:marketplace (ver PLUGIN_OBJECT_ALIAS em OpenFgaRebacPolicy.ts).
const PLUGIN_LABELS: Record<string, string> = {
  infrawallet:  'Costs (InfraWallet)',
  observability: 'Observabilidade (Grafana)',
  catalog:      'Catálogo',
  provisioned_resources: 'Recursos Provisionados',
  marketplace:  'Executar Templates (Marketplace)',
  kubernetes:   'Kubernetes',
  devtools:     'DevTools',
  'group-management': 'Gestão de Grupos',
};

// Rotas absolutas dos plugins — undefined quando o plugin não tem página própria.
const PLUGIN_ROUTES: Record<string, string | undefined> = {
  infrawallet:   '/costs',
  observability: '/observability',
  catalog:       '/provisioned-resources',
  provisioned_resources: '/provisioned-resources',
  marketplace:   '/marketplace',
  devtools:      '/devtools',
  kubernetes:    undefined, // exposto como tab em entidades, sem página dedicada
  'group-management': '/group-management',
};

function pluginLabel(id: string): string {
  return PLUGIN_LABELS[id] ?? id.charAt(0).toUpperCase() + id.slice(1);
}

function countByRelation(grants: Grant[], ...relations: GrantRelation[]): number {
  return grants.filter(g => relations.includes(g.relation)).length;
}

interface PluginEntry {
  id: string;
  grants: Grant[];
  canManage: boolean;
}

export function PluginAccessPage() {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const client = new RebacExplorerClient({ discoveryApi, fetchApi });

  const canAccessPage = useCanViewPlugin('access-management');
  const [plugins, setPlugins] = useState<PluginEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const objects = await client.getObjects({ type: 'plugin', limit: 100 });
        const ids = objects.map(o => o.split(':').slice(1).join(':'));

        const results = await Promise.all(
          ids.map(id =>
            client.getGrants('plugin', id)
              .then(data => ({ id, can_manage: data.can_manage, can_view: data.can_view, grants: data.grants }))
              .catch(() => null),
          ),
        );

        if (cancelled) return;

        // Exibe plugins onde o usuário tem ao menos can_view — admin vê tudo + pode gerenciar;
        // viewer vê a lista mas com botão de gestão desabilitado.
        const viewable = results
          .filter((r): r is { id: string; can_manage: boolean; can_view: boolean; grants: Grant[] } =>
            r !== null && (r.can_manage || r.can_view),
          )
          .map(r => ({ id: r.id, grants: r.grants, canManage: r.can_manage }));

        setPlugins(viewable);
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? 'Erro ao carregar plugins');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderBody = () => {
    if (loading) {
      return (
        <Box display="flex" alignItems="center" style={{ gap: 12, padding: 32 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="textSecondary">Carregando plugins…</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Typography
          variant="body2"
          style={{
            color: theme.palette.error.main,
            padding: '8px 12px',
            borderRadius: 4,
            border: `1px solid ${theme.palette.error.main}`,
            backgroundColor: isDark ? 'rgba(211,47,47,0.12)' : '#ffebee',
          }}
        >
          {error}
        </Typography>
      );
    }

    if (plugins.length === 0) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" style={{ padding: '48px 0', gap: 12 }}>
          <LockIcon style={{ fontSize: 40, color: theme.palette.text.disabled }} />
          <Typography variant="body1" color="textSecondary">
            Você não tem permissão em nenhum plugin.
          </Typography>
        </Box>
      );
    }

    return (
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {plugins.map(plugin => {
          const adminCount = countByRelation(plugin.grants, 'admin', 'owner');
          const viewerCount = countByRelation(plugin.grants, 'viewer');
          const editorCount = countByRelation(plugin.grants, 'editor');
          const executorCount = countByRelation(plugin.grants, 'executor');

          const parts: string[] = [];
          if (adminCount)   parts.push(`${adminCount} admin${adminCount > 1 ? 's' : ''}`);
          if (editorCount)  parts.push(`${editorCount} editor${editorCount > 1 ? 'es' : ''}`);
          if (executorCount) parts.push(`${executorCount} executor${executorCount > 1 ? 'es' : ''}`);
          if (viewerCount)  parts.push(`${viewerCount} viewer${viewerCount > 1 ? 's' : ''}`);
          const summary = parts.length > 0 ? parts.join(' · ') : 'Nenhuma permissão explícita';

          return (
            <Box
              key={plugin.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 16px',
                borderRadius: 8,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                gap: 16,
              }}
            >
              <Box>
                <Typography variant="subtitle1" style={{ fontWeight: 700, lineHeight: 1.3 }}>
                  {pluginLabel(plugin.id)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {summary}
                </Typography>
              </Box>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {PLUGIN_ROUTES[plugin.id] && (
                  <Tooltip title={`Ir para ${pluginLabel(plugin.id)}`}>
                    <Link to={PLUGIN_ROUTES[plugin.id]!} style={{ textDecoration: 'none' }}>
                      <Box
                        component="span"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '5px 12px',
                          borderRadius: 4,
                          border: `1px solid ${theme.palette.divider}`,
                          fontSize: 13,
                          fontWeight: 500,
                          color: theme.palette.text.primary,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Abrir
                        <OpenInNewIcon style={{ fontSize: 13 }} />
                      </Box>
                    </Link>
                  </Tooltip>
                )}
                <ManageAccessButton
                  resourceType="plugin"
                  resourceId={plugin.id}
                  label={pluginLabel(plugin.id)}
                  canManage={plugin.canManage}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  if (canAccessPage === undefined) {
    return (
      <Page themeId="tool">
        <Content>
          <Box display="flex" alignItems="center" style={{ gap: 12, padding: 48 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">Verificando permissões…</Typography>
          </Box>
        </Content>
      </Page>
    );
  }

  if (canAccessPage === false) {
    return (
      <Page themeId="tool">
        <Content>
          <Box display="flex" flexDirection="column" alignItems="center" style={{ padding: '64px 0', gap: 16 }}>
            <LockIcon style={{ fontSize: 48, color: theme.palette.text.disabled }} />
            <Typography variant="h6" color="textSecondary">Acesso negado</Typography>
            <Typography variant="body2" color="textSecondary">
              Você não tem permissão para acessar a gestão de plugins.
            </Typography>
          </Box>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Content>
        <Box style={{ maxWidth: 800, margin: '0 auto' }}>
          <Box style={{ marginBottom: 24 }}>
            <Typography variant="h4" style={{ fontWeight: 800 }}>
              Gestão de Acesso
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              Controle quem acessa cada plugin da plataforma.
            </Typography>
          </Box>
          <InfoCard title="Plugins">
            {renderBody()}
          </InfoCard>
        </Box>
      </Content>
    </Page>
  );
}
